import {
  useReactTable,
  getCoreRowModel,
  type ColumnDef,
  type SortingState,
  type OnChangeFn,
  type RowData,
} from "@tanstack/react-table";
import { type ReactNode } from "react";
import { DndContextProvider } from "./DndContextProvider";
import { TableContext } from "./TableContext";
import { useTableColumnOrder } from "./useTableColumnOrder";
import { useTableColumnPinning } from "./useTableColumnPinning";
import { useTableColumnVisibility } from "@/components/DataTable/useTableColumnVisibility";

interface TableRootProps<TData extends RowData> {
  data: TData[];
  columns: ColumnDef<TData, any>[];
  loading: boolean;
  error: string | undefined;
  tableId: string;
  sortConfig?: {
    sorting: SortingState;
    onSortingChange: OnChangeFn<SortingState>;
  };
  children: ReactNode;
}

export function TableRoot<TData extends RowData>(props: TableRootProps<TData>) {
  const { columnOrder, handleDragEnd, onColumnOrderChange } = useTableColumnOrder(props.tableId, props.columns);
  const { columnPinning, onColumnPinningChange } = useTableColumnPinning(props.tableId, columnOrder);

  return (
    <DndContextProvider onDragEnd={handleDragEnd}>
      <TableProvider
        {...props}
        columnOrder={columnOrder}
        onColumnOrderChange={onColumnOrderChange}
        columnPinning={columnPinning}
        onColumnPinningChange={onColumnPinningChange}
      />
    </DndContextProvider>
  );
}

function TableProvider<TData extends RowData>({
  data,
  columns,
  loading = false,
  error,
  tableId,
  sortConfig,
  children,
  columnOrder,
  onColumnOrderChange,
  columnPinning,
  onColumnPinningChange,
}: TableRootProps<TData> & {
  columnOrder: ReturnType<typeof useTableColumnOrder>["columnOrder"];
  onColumnOrderChange: ReturnType<typeof useTableColumnOrder>["onColumnOrderChange"];
  columnPinning: ReturnType<typeof useTableColumnPinning>["columnPinning"];
  onColumnPinningChange: ReturnType<typeof useTableColumnPinning>["onColumnPinningChange"];
}) {
  const { columnVisibility, onColumnVisibilityChange } = useTableColumnVisibility(tableId);

  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
    manualSorting: true,
    manualPagination: true,
    sortDescFirst: true,
    enableColumnResizing: true,
    columnResizeMode: "onChange",
    defaultColumn: {
      enableResizing: true,
      enableSorting: false,
    },
    onSortingChange: sortConfig?.onSortingChange,
    onColumnOrderChange,
    onColumnPinningChange,
    onColumnVisibilityChange,
    state: {
      sorting: sortConfig?.sorting,
      columnOrder,
      columnPinning,
      columnVisibility,
    },
  });

  const contextValue = {
    table,
    loading,
    error,
    columnOrder,
    columnPinning,
    onColumnPinningChange,
  };

  return <TableContext.Provider value={contextValue}>{children}</TableContext.Provider>;
}
